import { useState, type FormEvent, type ReactNode } from 'react';
import { Ic } from './Icon';
import type { LandingContent } from './content';

const SIZES = ['1-10', '11-50', '51-200', '200+'] as const;

interface LeadFormProps {
  c: LandingContent;
  idPrefix: string;
  /** Modal variant adds the e-mail field and uses the modal labels. */
  modal?: boolean;
  autoFocusName?: boolean;
  header?: ReactNode;
}

/**
 * Lead capture form. Demo only for now: there is no backend endpoint yet, so a valid submit
 * just flips into the "received" state.
 */
export function LeadForm({ c, idPrefix, modal = false, autoFocusName = false, header }: LeadFormProps) {
  const [done, setDone] = useState(false);
  const F = c.form;
  const id = (k: string) => `${idPrefix}-${k}`;

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }
    setDone(true);
  };

  return (
    <form className={`lp-form${done ? ' done' : ''}${modal ? '' : ' lp-card'}`} id={idPrefix} noValidate onSubmit={onSubmit} data-reveal={modal ? undefined : ''}>
      {header}
      <div className="field">
        <label htmlFor={id('name')}>{F.name}</label>
        <input id={id('name')} name="name" autoComplete="name" required autoFocus={autoFocusName} />
      </div>
      <div className="field">
        <label htmlFor={id('company')}>{F.company}</label>
        <input id={id('company')} name="company" autoComplete="organization" required />
      </div>
      <div className="field">
        <label htmlFor={id('phone')}>{modal ? c.modal.phone : F.phone}</label>
        <input id={id('phone')} name="phone" type="tel" autoComplete="tel" dir="ltr" required />
      </div>
      {modal ? (
        <div className="field">
          <label htmlFor={id('email')}>{c.modal.email}</label>
          <input id={id('email')} name="email" type="email" autoComplete="email" dir="ltr" />
        </div>
      ) : null}
      <div className={`field${modal ? ' full' : ''}`}>
        <label htmlFor={id('size')}>{F.size}</label>
        <select id={id('size')} name="size" defaultValue="11-50">
          {SIZES.map((s) => (
            <option value={s} key={s}>
              {s.replace('-', ' – ')}
            </option>
          ))}
        </select>
      </div>
      <div className="field full">
        <label htmlFor={id('notes')}>{F.notes}</label>
        <textarea id={id('notes')} name="notes" style={modal ? { minHeight: 72 } : undefined} />
      </div>
      <button className="lp-btn lp-btn-primary lp-btn-lg full" type="submit" style={modal ? { width: '100%' } : undefined}>
        <span>{modal ? c.modal.submit : F.submit}</span>
        <Ic name="arrow" flip />
        <span className="shine" />
      </button>
      <div className="lp-form-ok" role="status" aria-live="polite">
        <div className="ic">
          <Ic name="check" />
        </div>
        <h3>{F.okTitle}</h3>
        <p style={{ color: 'var(--muted)', marginTop: 8 }}>{modal ? c.modal.okSub : F.okSub}</p>
      </div>
    </form>
  );
}

/** Pre-footer CTA banner + inline contact form. */
export function CtaBanner({ c, onOpenDemo }: { c: LandingContent; onOpenDemo: () => void }) {
  return (
    <section className="lp-section" id="contact" style={{ paddingTop: 0 }}>
      <div className="lp-container">
        <div className="cta" data-reveal>
          <div className="cta-grid" aria-hidden="true" />
          <div className="proof">
            <span className="avs" aria-hidden="true">
              <i />
              <i />
              <i />
            </span>
            <span>
              <b>120+</b> <span>{c.cta.proof}</span>
            </span>
          </div>
          <h2>{c.cta.title}</h2>
          <p>{c.cta.sub}</p>
          <div className="btns">
            <button type="button" className="lp-btn lp-btn-primary lp-btn-lg" onClick={onOpenDemo}>
              <Ic name="zap" />
              <span>{c.cta.b1}</span>
            </button>
            <a className="lp-btn lp-btn-ghost lp-btn-lg" href="#demoForm">
              <Ic name="call" />
              <span>{c.cta.b2}</span>
            </a>
          </div>
          <p className="hot">
            <span>{c.cta.hot}</span> <b>+20 100 000 0000</b>
          </p>
        </div>

        <LeadForm
          c={c}
          idPrefix="demoForm"
          header={
            <div className="full" style={{ marginBottom: 6 }}>
              <h3 style={{ fontSize: 21 }}>{c.form.title}</h3>
              <p style={{ color: 'var(--muted)', fontSize: 14, marginTop: 6 }}>{c.form.sub}</p>
            </div>
          }
        />
      </div>
    </section>
  );
}
